import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, CardContent, Typography, Alert, Paper,
  TextField, Snackbar, CircularProgress, Select, MenuItem,
  Stack, Divider, Backdrop, Button,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import {
  Timeline as ProjectionIcon,
  CalendarMonth as CalendarIcon, KeyboardArrowDown as ArrowIcon,
  EventNote as CasesIcon, AccountBalance as BudgetIcon,
  Groups as StaffIcon, Settings as OpsIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { PageContainer, PageCard } from '../../common/components/styled-ui-elements';
import { CenterSelector } from '../../common/components';
import { useCenterSelection } from '../../common/hooks/useCenterSelection';
import {
  useGetProjectionsQuery,
  useUpsertProjectionMutation,
} from '../../../services/LaborManagementService';
import type {
  DepartmentDayActualUpsertProjectionView,
  DailyOperationalInputUpsertView,
  ProjectionConflictView,
} from '../../../services/LaborManagementService/types/laborManagementTypes';
import StaffingSummaryGrid from '../components/projections/StaffingSummaryGrid';
import type { ActualsSummary } from '../components/projections/StaffingSummaryGrid';
import DailyStaffingGrid from '../components/projections/DailyStaffingGrid';
import { StaleSaveAlert } from '../components/projections';
import { computeCostToDate, todayIsoDate } from '../components/projections/costToDate';

// ── Formatters ──────────────────────────────────────────────────────

const fmtCurrency = (v: number) =>
  v.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const fmtNum = (v: number) => v.toLocaleString('en-US', { maximumFractionDigits: 2 });
const buildDayDepartmentKey = (dayOfWeek: number, departmentName: string) => `${dayOfWeek}|${departmentName}`;
const quantise = (v: number) => Math.round(v * 100) / 100;
// Empty means "not entered" and must stay distinct from an entered value, so only compare
// numerically once both sides are non-empty and parse; otherwise fall back to the raw strings.
const isSameEditValue = (current: string, original: string) => {
  if (current === '' || original === '') return current === original;
  const currentNum = Number.parseFloat(current);
  const originalNum = Number.parseFloat(original);
  if (Number.isNaN(currentNum) || Number.isNaN(originalNum)) return current === original;
  return quantise(currentNum) === quantise(originalNum);
};

// A refusal arrives as an RTK Query error carrying the conflict body, not as a thrown Error.
const asConflict = (error: unknown): ProjectionConflictView | null => {
  const rejection = error as { status?: unknown; data?: ProjectionConflictView } | undefined;
  return rejection?.status === 409 && rejection.data ? rejection.data : null;
};

// ── ISO 8601 week of current date (client clock) ────────────────────

const getCurrentIsoWeek = (): { isoYear: number; isoWeek: number } => {
  const d = new Date();
  // Shift to Thursday of the current week (ISO weeks are Mon–Sun, anchored on Thursday)
  const target = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = (target.getUTCDay() + 6) % 7; // Mon=0..Sun=6
  target.setUTCDate(target.getUTCDate() - dayNum + 3);
  const firstThursday = new Date(Date.UTC(target.getUTCFullYear(), 0, 4));
  const diff = (target.getTime() - firstThursday.getTime()) / 86400000;
  const isoWeek = 1 + Math.round((diff - 3 + ((firstThursday.getUTCDay() + 6) % 7)) / 7);
  return { isoYear: target.getUTCFullYear(), isoWeek };
};

// ── KPI Card ────────────────────────────────────────────────────────

interface KpiCardProps {
  label: string;
  value: string;
  detail?: string;
  accentColor: string;
  testId: string;
}

const KpiCard: React.FC<KpiCardProps> = ({ label, value, detail, accentColor, testId }) => (
  <Paper elevation={0} data-testid={testId} sx={{
    flex: 1, p: 2, borderTop: `3px solid ${accentColor}`,
    backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderTopColor: accentColor,
  }}>
    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
      {label}
    </Typography>
    <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b', lineHeight: 1.3, mt: 0.5 }}>
      {value}
    </Typography>
    {detail && (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
        {detail}
      </Typography>
    )}
  </Paper>
);

// ── Section Header (matches SectionHeader pattern) ──────────────────

const SectionLabel: React.FC<{ icon: React.ReactNode; title: string }> = ({ icon, title }) => (
  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
    {icon}{title}
  </Typography>
);

// ── Last Saved Line ─────────────────────────────────────────────────
// `lastSavedAt` is absent for a week with no stored document, and `lastSavedBy` is empty/null for
// every week saved before this feature shipped — truthiness on both keeps either from rendering a
// dangling label.

const LastSavedLine: React.FC<{ lastSavedBy?: string | null; lastSavedAt?: string | null }> = ({ lastSavedBy, lastSavedAt }) => {
  if (!lastSavedAt) return null;
  const savedAt = new Date(lastSavedAt).toLocaleString();
  return (
    <Typography variant="caption" color="text.secondary" data-testid="last-saved-line">
      {lastSavedBy ? `Last saved by ${lastSavedBy} on ${savedAt}` : `Last saved on ${savedAt}`}
    </Typography>
  );
};

// ── Main Page ───────────────────────────────────────────────────────

const LaborProjections: React.FC = () => {
  const [searchParams] = useSearchParams();
  const company = searchParams.get('company') || '';
  const center = searchParams.get('center') || '';
  const { handleCenterChange } = useCenterSelection();

  const [selectedWeek, setSelectedWeek] = useState<{ isoYear: number; isoWeek: number }>(getCurrentIsoWeek);

  const skip = !company || !center;
  const {
    data,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetProjectionsQuery(
    {
      company,
      centerId: center,
      isoYear: selectedWeek.isoYear,
      isoWeek: selectedWeek.isoWeek,
    },
    { skip },
  );

  const handleWeekChange = useCallback((event: SelectChangeEvent<string>) => {
    const [y, w] = event.target.value.split('-').map(Number);
    setSelectedWeek({ isoYear: y, isoWeek: w });
  }, []);

  const [upsertProjection, { isLoading: isSaving }] = useUpsertProjectionMutation();
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [saveError, setSaveError] = useState<string | null>(null);
  const [expectedVersion, setExpectedVersion] = useState<string | null>(null);
  const [conflict, setConflict] = useState<ProjectionConflictView | null>(null);



  // ── Actual staff + comment state ──────────────────────────────────

  const [actualEdits, setActualEdits] = useState<Record<string, string>>({});
  const [dailyInputEdits, setDailyInputEdits] = useState<Record<number, { rooms: string; trays: string }>>({});
  const [commentText, setCommentText] = useState('');
  const [initialized, setInitialized] = useState(false);

  // A conflict belongs to one week of one center. Moving to another abandons it — and must, or the
  // re-sync guard below would freeze the old week's typed values over the new week's data.
  useEffect(() => {
    setConflict(null);
  }, [company, center, selectedWeek]);

  // Track data identity so we re-sync local state whenever the server returns
  // new data — whether from a week change OR an invalidation-triggered refetch.
  const prevDataRef = useRef(data);
  useEffect(() => {
    // A refusal must leave the manager's typed values alone. The optimistic update's undo() changes
    // the query-data reference by itself, so without this guard a 409 resets every cell on its own.
    if (conflict) return;
    if (!data?.week) {
      setInitialized(false);
      prevDataRef.current = data;
      return;
    }
    // Skip if the data object reference hasn't changed (no new server data)
    if (data === prevDataRef.current && initialized) return;
    prevDataRef.current = data;

    const edits: Record<string, string> = {};
    const dailyEdits: Record<number, { rooms: string; trays: string }> = {};
    for (const day of data.week.dailyStaffing ?? []) {
      for (const dept of day.departments) {
        edits[buildDayDepartmentKey(day.dayOfWeek, dept.departmentName)] = dept.actualStaff != null ? String(dept.actualStaff) : '';
      }
      dailyEdits[day.dayOfWeek] = {
        rooms: day.roomsEntered ? String(day.rooms) : '',
        trays: day.traysEntered ? String(day.trays) : '',
      };
    }
    setActualEdits(edits);
    setDailyInputEdits(dailyEdits);
    setCommentText(data.week.staffingComment ?? '');
    setSaveError(null);
    setExpectedVersion(data.week.version ?? null);
    setInitialized(true);
  }, [data, initialized, conflict]);

  const handleActualChange = useCallback((dayOfWeek: number, deptName: string, value: string) => {
    setActualEdits((prev) => ({
      ...prev,
      [buildDayDepartmentKey(dayOfWeek, deptName)]: value,
    }));
  }, []);

  const handleRoomsChange = useCallback((dayOfWeek: number, value: string) => {
    setDailyInputEdits((prev) => ({
      ...prev,
      [dayOfWeek]: { ...(prev[dayOfWeek] ?? { rooms: '', trays: '' }), rooms: value },
    }));
  }, []);

  const handleTraysChange = useCallback((dayOfWeek: number, value: string) => {
    setDailyInputEdits((prev) => ({
      ...prev,
      [dayOfWeek]: { ...(prev[dayOfWeek] ?? { rooms: '', trays: '' }), trays: value },
    }));
  }, []);

  const roomsEdits = useMemo(() => {
    const flat: Record<number, string> = {};
    for (const [dayOfWeek, edit] of Object.entries(dailyInputEdits)) {
      flat[Number(dayOfWeek)] = edit.rooms;
    }
    return flat;
  }, [dailyInputEdits]);

  const traysEdits = useMemo(() => {
    const flat: Record<number, string> = {};
    for (const [dayOfWeek, edit] of Object.entries(dailyInputEdits)) {
      flat[Number(dayOfWeek)] = edit.trays;
    }
    return flat;
  }, [dailyInputEdits]);

  // #10: Compute dirty flag as a memoized value instead of calling a function on every render
  const hasUnsavedChanges = useMemo(() => {
    if (!data?.week || !initialized) return false;
    for (const day of data.week.dailyStaffing ?? []) {
      for (const dept of day.departments) {
        const key = buildDayDepartmentKey(day.dayOfWeek, dept.departmentName);
        const current = actualEdits[key] ?? '';
        const original = dept.actualStaff != null ? String(dept.actualStaff) : '';
        if (!isSameEditValue(current, original)) return true;
      }
      const currentRooms = dailyInputEdits[day.dayOfWeek]?.rooms ?? '';
      const originalRooms = day.roomsEntered ? String(day.rooms) : '';
      if (!isSameEditValue(currentRooms, originalRooms)) return true;
      const currentTrays = dailyInputEdits[day.dayOfWeek]?.trays ?? '';
      const originalTrays = day.traysEntered ? String(day.trays) : '';
      if (!isSameEditValue(currentTrays, originalTrays)) return true;
    }
    if ((commentText ?? '') !== (data.week.staffingComment ?? '')) return true;
    return false;
  }, [data, actualEdits, dailyInputEdits, commentText, initialized]);

  const submitSave = useCallback(async (versionToSend: string | null) => {
    if (!company || !center || !data?.week) return;

    const dayActuals: DepartmentDayActualUpsertProjectionView[] = [];
    for (const day of data.week.dailyStaffing ?? []) {
      for (const dept of day.departments) {
        const key = buildDayDepartmentKey(day.dayOfWeek, dept.departmentName);
        // An empty or unparseable cell is "not entered" (null), never 0 — 0 is a real entry that
        // prices the day at nothing and hides the em dash downstream.
        const parsedActual = Number.parseFloat(actualEdits[key] ?? '');
        dayActuals.push({
          departmentName: dept.departmentName,
          dayOfWeek: day.dayOfWeek,
          actualStaff: Number.isNaN(parsedActual) ? null : parsedActual,
        });
      }
    }

    const dailyOperationalInputs: DailyOperationalInputUpsertView[] = (data.week.dailyStaffing ?? []).map((day) => {
      const edit = dailyInputEdits[day.dayOfWeek];
      // Unparseable falls back to null ("not entered"), never 0 — 0 would zero the day's staffing.
      const rooms = Number.parseFloat(edit?.rooms ?? '');
      const trays = Number.parseFloat(edit?.trays ?? '');
      return {
        dayOfWeek: day.dayOfWeek,
        rooms: Number.isNaN(rooms) ? null : quantise(rooms),
        trays: Number.isNaN(trays) ? null : quantise(trays),
      };
    });

    try {
      const saved = await upsertProjection({
        company,
        centerId: center,
        isoYear: data.week.isoYear,
        isoWeek: data.week.isoWeek,
        departmentOverrides: data.week.departmentOverrides ?? [],
        departmentDayActuals: dayActuals,
        staffingComment: commentText,
        dailyOperationalInputs,
        expectedVersion: versionToSend,
      }).unwrap();
      setConflict(null);
      setSaveError(null);
      setExpectedVersion(saved?.version ?? null);
      setSnackbar({ open: true, message: 'Projection saved.', severity: 'success' });
    } catch (error) {
      const refusal = asConflict(error);
      if (refusal) {
        // A refusal never shows the generic failure text, including after an earlier failure set it.
        setSaveError(null);
        setConflict(refusal);
        setSnackbar({ open: true, message: 'This copy of the week is out of date.', severity: 'error' });
        return;
      }
      setSaveError('Failed to save projection. Your edits are still available.');
      setSnackbar({ open: true, message: 'Failed to save projection.', severity: 'error' });
    }
  }, [company, center, data, actualEdits, dailyInputEdits, commentText, upsertProjection]);

  const handleSave = useCallback(() => submitSave(expectedVersion), [submitSave, expectedVersion]);
  const handleReload = useCallback(() => { setConflict(null); refetch(); }, [refetch]);
  const handleSaveAnyway = useCallback(() => {
    if (conflict) submitSave(conflict.currentVersion);
  }, [conflict, submitSave]);

  const week = data?.week;
  const isProjectionBusy = isLoading || isFetching;
  const isProjectionReady = Boolean(week?.dailyStaffing?.length === 7) && !isProjectionBusy && !isSaving;

  // ── Actuals summary (daily rollup from current cell values) ───────

  const actualsSummary: ActualsSummary | null = useMemo(() => {
    const hasAny = Object.values(actualEdits).some((v) => v !== '');
    if (!hasAny || !week?.budget) return null;

    const totalStaff = (week.dailyStaffing ?? []).reduce((dayTotal, day) => {
      const departmentTotal = day.departments.reduce((deptTotal, dept) => {
        const key = buildDayDepartmentKey(day.dayOfWeek, dept.departmentName);
        const value = actualEdits[key] ?? '';
        return deptTotal + (value === '' ? 0 : parseFloat(value) || 0);
      }, 0);

      return dayTotal + departmentTotal;
    }, 0);

    const opDayLen = week.budget.operatingDayLength;
    const costPerHour = week.budget.costPerHour;
    const effectiveCases = week.effectiveCases;
    const totalHours = totalStaff * opDayLen;
    const hoursPerCase = effectiveCases > 0 ? totalHours / effectiveCases : 0;
    const costPerCase = hoursPerCase * costPerHour;
    const totalCost = totalHours * costPerHour;
    return { totalStaff, totalHours, hoursPerCase, costPerCase, totalCost };
  }, [actualEdits, week]);

  const costToDate = useMemo(() => {
    if (!week?.staffing || !week?.budget) return null;
    return computeCostToDate(week.dailyStaffing ?? [], week.budget, todayIsoDate());
  }, [week]);

  const weekValue = `${selectedWeek.isoYear}-${selectedWeek.isoWeek}`;

  return (
    <PageContainer>
      <Backdrop open={isSaving} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1, flexDirection: 'column', gap: 2 }}>
        <CircularProgress color="inherit" size={56} />
        <Typography variant="h6">Saving…</Typography>
      </Backdrop>

      <Box display="flex" justifyContent="center">
        <PageCard>
          {/* Header */}
          <Box sx={{
            backgroundColor: '#ffffff',
            borderBottom: '2px solid #3b82f6',
            p: 2.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <ProjectionIcon sx={{ fontSize: 32, color: '#3b82f6' }} />
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#1e293b' }}>
                  Weekly Projection
                </Typography>
                <LastSavedLine lastSavedBy={week?.lastSavedBy} lastSavedAt={week?.lastSavedAt} />
              </Box>
            </Box>
            <Button
                variant="contained"
                size="small"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={isSaving || !isProjectionReady || !hasUnsavedChanges}
                sx={{ textTransform: 'none' }}
              >
                Save
              </Button>
          </Box>

          {/* Content */}
          <CardContent sx={{ p: 3, backgroundColor: '#ffffff' }}>
            {/* Controls Row: Center + Week Selector */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <CenterSelector value={center} onChange={handleCenterChange} />

              {data?.availableWeeks && data.availableWeeks.length > 0 && (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  sx={{
                    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                    borderRadius: 3,
                    px: 2,
                    py: 0.75,
                    border: '2px solid #cbd5e1',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                    transition: 'all 0.2s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: '#94a3b8',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  <Box sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: 32, height: 32, borderRadius: '50%', backgroundColor: '#64748b', flexShrink: 0,
                  }}>
                    <CalendarIcon sx={{ color: '#ffffff', fontSize: 18 }} />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{
                      color: '#64748b', fontWeight: 700, textTransform: 'uppercase',
                      letterSpacing: 1, fontSize: '0.65rem', lineHeight: 1, display: 'block',
                    }}>
                      Week
                    </Typography>
                    <Select<string>
                      size="small"
                      value={weekValue}
                      onChange={handleWeekChange}
                      variant="standard"
                      disableUnderline
                      IconComponent={ArrowIcon}
                      sx={{
                        '& .MuiSelect-select': { py: 0, pr: '28px !important', fontWeight: 700, color: '#1e293b', fontSize: '0.95rem' },
                        '& .MuiSvgIcon-root': { color: '#64748b', fontSize: 22 },
                        '& .MuiSelect-select:focus': { backgroundColor: 'transparent' },
                      }}
                    >
                      {data.availableWeeks.map((w) => (
                        <MenuItem key={`${w.isoYear}-${w.isoWeek}`} value={`${w.isoYear}-${w.isoWeek}`}>
                          {w.weekLabel}{w.isCurrentWeek ? ' (Current)' : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                </Stack>
              )}
            </Box>

            {/* States: empty, loading, error */}
            {!company || !center ? (
              <Alert severity="info">Select a center to view weekly projection.</Alert>
            ) : isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : isError ? (
              <Alert
                severity="error"
                action={<Button color="inherit" size="small" onClick={() => refetch()}>Retry</Button>}
              >
                Failed to load projection.
              </Alert>
            ) : !week ? (
              <Alert severity="info">No projection data available for this week.</Alert>
            ) : (
              <>
                {conflict && (
                  <Box sx={{ mb: 2 }}>
                    <StaleSaveAlert conflict={conflict} onReload={handleReload} onSaveAnyway={handleSaveAnyway} />
                  </Box>
                )}
                {/* KPI Cards Row */}
                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                  <KpiCard
                    label="Scheduled Cases"
                    value={String(week.scheduledCases)}
                    accentColor="#3b82f6"
                    testId="kpi-scheduled-cases"
                  />
                  <KpiCard
                    label="Total Staff"
                    value={week.staffing ? fmtNum(week.staffing.totalDirectStaff) : '—'}
                    detail={week.staffing ? `${fmtNum(week.staffing.clinicalHoursPerCase)} hrs/case` : undefined}
                    accentColor="#8b5cf6"
                    testId="kpi-total-staff"
                  />
                  <KpiCard
                    label="Cost to Date"
                    value={costToDate ? fmtCurrency(costToDate.cost) : '—'}
                    detail={costToDate ? `${fmtCurrency(costToDate.cases > 0 ? costToDate.cost / costToDate.cases : 0)}/case` : undefined}
                    accentColor="#e11d48"
                    testId="kpi-cost-to-date"
                  />
                  <KpiCard
                    label="Calc Cost"
                    value={week.staffing ? fmtCurrency(week.staffing.totalCost) : '—'}
                    detail={week.staffing ? `${fmtCurrency(week.staffing.costPerCase)}/case` : undefined}
                    accentColor="#f59e0b"
                    testId="kpi-calc-cost"
                  />
                  <KpiCard
                    label="Actual Cost"
                    value={actualsSummary ? fmtCurrency(actualsSummary.totalCost) : '—'}
                    detail={actualsSummary ? `${fmtCurrency(actualsSummary.costPerCase)}/case` : undefined}
                    accentColor="#0ea5a4"
                    testId="kpi-actual-cost"
                  />
                </Box>

                {/* Inputs Row Above Staffing Breakdown */}
                {week.budget ? (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', lg: 'repeat(3, minmax(0, 1fr))' },
                      gap: 2,
                      mb: 3,
                      alignItems: 'stretch',
                      '& > *': {
                        minWidth: 0,
                      },
                    }}
                  >
                    <Paper elevation={0} sx={{ p: 1.5, height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 1.5 }}>
                      <SectionLabel icon={<OpsIcon sx={{ fontSize: 20, color: '#64748b' }} />} title="Operational Inputs" />
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 0.75 }}>
                        <Typography variant="body2" color="text.secondary">Avg Case Duration</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right' }}>{fmtNum(week.budget.avgCaseDurationMin)} min</Typography>
                        <Typography variant="body2" color="text.secondary">Operating Day Length</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right' }}>{fmtNum(week.budget.operatingDayLength)} hrs</Typography>
                        <Typography variant="body2" color="text.secondary">Cost / Hour</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right' }}>{fmtCurrency(week.budget.costPerHour)}</Typography>
                        <Typography variant="body2" color="text.secondary">Operating Days</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right' }}>{week.budget.operatingDaysInWeek}</Typography>
                      </Box>
                    </Paper>

                    <Paper elevation={0} sx={{ p: 1.5, height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 1.5 }}>
                      <SectionLabel icon={<BudgetIcon sx={{ fontSize: 20, color: '#64748b' }} />} title="Budget Allocation" />
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 0.75 }}>
                        <Typography variant="body2" color="text.secondary">Cases</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right' }}>{week.budget.cases}</Typography>
                        <Typography variant="body2" color="text.secondary">Hours</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right' }}>{fmtNum(week.budget.hours)}</Typography>
                        <Typography variant="body2" color="text.secondary">Cost</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right' }}>{fmtCurrency(week.budget.cost)}</Typography>
                        <Typography variant="body2" color="text.secondary">Hrs/Case</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right' }}>{fmtNum(week.budget.hoursPerCase)}</Typography>
                        <Typography variant="body2" color="text.secondary">Cost/Case</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right' }}>{week.budget.cases > 0 ? fmtCurrency(week.budget.cost / week.budget.cases) : '—'}</Typography>
                      </Box>
                    </Paper>

                    <Paper elevation={0} sx={{ p: 1.5, height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 1.5 }}>
                      <SectionLabel icon={<CasesIcon sx={{ fontSize: 20, color: '#64748b' }} />} title="Staffing Notes" />
                      <TextField
                        label="Staffing Notes"
                        fullWidth
                        multiline
                        rows={4}
                        size="small"
                        placeholder="Describe why actual staffing differs from calculated…"
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                        disabled={!isProjectionReady}
                        InputProps={{
                          sx: {
                            alignItems: 'flex-start',
                            '& .MuiInputBase-inputMultiline': {
                              whiteSpace: 'pre-wrap',
                            },
                          },
                        }}
                        InputLabelProps={{
                          sx: {
                            '&.MuiInputLabel-shrink': {
                              transform: 'translate(14px, -9px) scale(0.75)',
                            },
                          },
                        }}
                      />
                    </Paper>
                  </Box>
                ) : (
                  <Alert severity="info" sx={{ py: 0.5, mb: 3 }}>No budget data available for this week.</Alert>
                )}

                {/* Full-Width Staffing Breakdown */}
                <Box sx={{ minWidth: 0, mt: 0.5 }}>
                  {week.staffing ? (
                    <Paper elevation={0} sx={{ p: 1.5, pt: 2, backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 1.5, minWidth: 0, overflow: 'hidden' }}>
                      <SectionLabel icon={<StaffIcon sx={{ fontSize: 20, color: '#64748b' }} />} title="Staffing Breakdown" />

                      <StaffingSummaryGrid calculated={week.staffing} actualsSummary={actualsSummary} />

                      <Divider sx={{ mb: 1 }} />

                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.25 }}>
                        Daily Staffing
                      </Typography>
                      <DailyStaffingGrid
                        days={week.dailyStaffing ?? []}
                        actualEdits={actualEdits}
                        onActualChange={handleActualChange}
                        roomsEdits={roomsEdits}
                        onRoomsChange={handleRoomsChange}
                        traysEdits={traysEdits}
                        onTraysChange={handleTraysChange}
                        disabled={!isProjectionReady}
                        isLoading={isProjectionBusy}
                        errorMessage={saveError}
                        onRetry={conflict ? undefined : handleSave}
                      />
                    </Paper>
                  ) : (
                    <Alert severity="info" sx={{ py: 0.5 }}>No staffing data available for this week.</Alert>
                  )}
                </Box>
              </>
            )}
          </CardContent>
        </PageCard>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default LaborProjections;
